import { useCallback, useRef, useState } from 'react';
import { ModuleData } from '../../Types/containerV2';
import { isFormModuleContent } from '../../Types/containerV2/modules/form';
import { FormSubmission, FORM_SETTING_KEYS } from '../../Types/forms';
import { getFormForUser, submitForm } from '../../Services/form';
import { useBizQuery, useBizMutation } from '../../Hooks/useBizQuery';
import { useFormContext } from '../Form/FormContext';
import FormRenderer from '../Form/FormRenderer';
import { useContainerContext } from './ContainerContext';
import './FormModule.scss';

type FormModuleProps = {
	module: ModuleData;
};

const FormModule = ({ module }: FormModuleProps) => {
	if (!isFormModuleContent(module.content)) {
		return null;
	}

	const { formKey, buttonText, preSubmitText, postSubmitText } = module.content;

	return (
		<FormModuleInner
			key={formKey}
			moduleId={module.id}
			formKey={formKey}
			buttonText={buttonText}
			preSubmitText={preSubmitText}
			postSubmitText={postSubmitText}
		/>
	);
};

type FormModuleInnerProps = {
	moduleId: number;
	formKey: string;
	buttonText: string;
	preSubmitText: string;
	postSubmitText: string;
};

const FormModuleInner = ({ moduleId, formKey, buttonText, preSubmitText, postSubmitText }: FormModuleInnerProps) => {
	const { finishModule, onFormSubmissionSuccess } = useContainerContext();
	const { mutateAsync } = useBizMutation(submitForm);
	const submissionInFlight = useRef(false);
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const formContextRef = useRef<{ validate: () => boolean; getSubmission: () => FormSubmission | null }>(null);

	const { data: form, statusState } = useBizQuery(['form', formKey] as const, () => getFormForUser(formKey), !!formKey);

	const allowMultipleSubmissions =
		form?.settings.find((s) => s.key === FORM_SETTING_KEYS.ALLOW_MULTIPLE_SUBMISSIONS)?.value === 'true';

	const alreadySubmitted = !allowMultipleSubmissions && (form?.currentUserSubmissionCount ?? 0) > 0;

	const handleSubmit = useCallback(async () => {
		if (!form || submissionInFlight.current || submitted || alreadySubmitted) return;

		const ctx = formContextRef.current;
		if (!ctx) return;

		if (!ctx.validate()) return;

		const submission = ctx.getSubmission();
		if (!submission) return;

		submissionInFlight.current = true;
		setSubmitting(true);
		setSubmitError(null);

		try {
			const result = await mutateAsync(submission);
			setSubmitted(true);
			try {
				const trackingId = form.settings.find((s) => s.key.toUpperCase() === FORM_SETTING_KEYS.TRACKING_ID)?.value.trim();
				if (trackingId) onFormSubmissionSuccess?.({ trackingId });
			} catch {
				// Tracking must not turn a saved submission into an error.
			}
			finishModule(moduleId, result.isPassed);
		} catch (error) {
			submissionInFlight.current = false;
			setSubmitError(error instanceof Error ? error.message : 'Failed to submit form. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}, [form, submitted, alreadySubmitted, mutateAsync, finishModule, moduleId, onFormSubmissionSuccess]);

	if (!formKey) {
		return <div className='biz-module biz-form-module'>No form configured.</div>;
	}

	if (statusState === 'LOADING') {
		return <div className='biz-module biz-form-module biz-form-module--loading' />;
	}

	if (statusState === 'ERROR' || !form) {
		return <div className='biz-module biz-form-module'>Failed to load form.</div>;
	}

	if (submitted) {
		return (
			<div className='biz-module biz-form-module biz-form-module--submitted'>
				{postSubmitText && <div className='biz-form-module__post-text'>{postSubmitText}</div>}
			</div>
		);
	}

	if (alreadySubmitted) {
		return (
			<div className='biz-module biz-form-module biz-form-module--already-submitted'>
				{postSubmitText && <div className='biz-form-module__post-text'>{postSubmitText}</div>}
			</div>
		);
	}

	return (
		<div className='biz-module biz-form-module'>
			{preSubmitText && <div className='biz-form-module__pre-text'>{preSubmitText}</div>}

			<FormRendererWithRef form={form} ref={formContextRef} />

			{submitError && <div className='biz-form-module__error'>{submitError}</div>}

			<div className='biz-form-module__submit'>
				<button
					type='button'
					className='biz-btn biz-primary biz-form-module__submit-btn'
					onClick={handleSubmit}
					disabled={submitting}
				>
					{submitting ? '...' : buttonText || 'Submit'}
				</button>
			</div>
		</div>
	);
};

/**
 * Wrapper around FormRenderer that exposes validate + getSubmission to the parent
 * via a ref, so the submit button can trigger validation from outside the form context.
 */
import { forwardRef, useImperativeHandle } from 'react';
import { FormData as FormDataType } from '../../Types/forms';

type FormRendererHandle = {
	validate: () => boolean;
	getSubmission: () => FormSubmission | null;
};

type FormRendererWithRefProps = {
	form: FormDataType;
};

const FormRendererWithRef = forwardRef<FormRendererHandle, FormRendererWithRefProps>(({ form }, ref) => {
	return (
		<FormRenderer form={form}>
			<FormRendererBridge ref={ref} />
		</FormRenderer>
	);
});

FormRendererWithRef.displayName = 'FormRendererWithRef';

const FormRendererBridge = forwardRef<FormRendererHandle>((_, ref) => {
	const { form, values, validate, hiddenFields } = useFormContext();

	useImperativeHandle(
		ref,
		() => ({
			validate,
			getSubmission: () => {
				const submissionValues = form.fields
					.filter((f) => !hiddenFields.has(f.key) && f.typeKey !== 'label' && f.typeKey !== 'video')
					.map((f) => ({
						formFieldKey: f.key,
						formFieldId: f.id,
						value: values[f.key] ?? '',
					}));

				return {
					formId: form.id,
					values: submissionValues,
				};
			},
		}),
		[form, values, validate, hiddenFields]
	);

	return null;
});

FormRendererBridge.displayName = 'FormRendererBridge';

export default FormModule;
